import { useMemo } from "react";
import { Link } from "react-router";
import { motion } from "motion/react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";

/**
 * Option V · TdB agent.
 *
 * Vue d'ensemble agent : heures du mois en cours, prochaines vacations,
 * et lien vers les pointages historiques.
 */
export function StatsPage() {
  const { user, logout } = useAuth();

  const monthRange = useMemo(() => {
    const d = new Date();
    const from = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    const to = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const nextRange = useMemo(() => {
    const now = new Date();
    const in7 = new Date(now.getTime() + 7 * 86400_000);
    return { from: now.toISOString(), to: in7.toISOString() };
  }, []);

  const meAgent = useQuery({
    queryKey: ["stats-me-agent"],
    queryFn: () => api.me.getAgent(),
    retry: false,
  });

  const monthVacations = useQuery({
    queryKey: ["stats-month-vacations", monthRange.from, monthRange.to],
    queryFn: () => api.me.vacations(monthRange),
  });

  const nextVacations = useQuery({
    queryKey: ["stats-next-vacations", nextRange.from, nextRange.to],
    queryFn: () => api.me.vacations(nextRange),
  });

  const monthHours = useMemo(() => {
    let h = 0;
    for (const v of monthVacations.data ?? []) {
      if (v.statut === "annulee") continue;
      h += Number(v.dureeHeures ?? 0);
    }
    return h;
  }, [monthVacations.data]);

  const monthNb = useMemo(
    () =>
      (monthVacations.data ?? []).filter((v) => v.statut !== "annulee").length,
    [monthVacations.data],
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <header className="sticky top-0 z-10 border-b border-navy-100 bg-white/90 px-5 py-3 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
              Bonjour {user?.prenom ?? ""}
            </p>
            <h1 className="font-display text-xl font-black tracking-tight text-navy-800">
              Mon tableau de bord
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="rounded-full bg-navy-50 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-navy-100"
            >
              Ma journée
            </Link>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-navy-600 hover:bg-navy-50"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-5 px-4 py-5">
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-3"
        >
          <Card
            label="Heures du mois"
            value={`${monthHours.toFixed(1)} h`}
            accent
          />
          <Card label="Vacations du mois" value={String(monthNb)} />
          <Card
            label="Matricule"
            value={meAgent.data?.matricule ?? "-"}
            small
          />
          <Card
            label="Spécialités"
            value={(meAgent.data?.specialites ?? []).join(", ") || "-"}
            small
          />
        </motion.section>

        <section>
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-navy-500">
            Prochaines 7 jours
          </h2>
          {nextVacations.isLoading ? (
            <p className="text-sm text-navy-400">Chargement…</p>
          ) : (nextVacations.data ?? []).length === 0 ? (
            <p className="rounded-2xl border border-dashed border-navy-200 p-4 text-center text-sm text-navy-400">
              Aucune vacation à venir.
            </p>
          ) : (
            <ul className="space-y-2">
              {(nextVacations.data ?? [])
                .sort(
                  (a, b) =>
                    new Date(a.debut).getTime() - new Date(b.debut).getTime(),
                )
                .map((v) => (
                  <li
                    key={v.id}
                    className="rounded-2xl border border-navy-100 bg-white p-3 shadow-sm"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
                      {new Date(v.debut).toLocaleDateString("fr-FR", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </p>
                    <p className="mt-1 font-bold text-navy-800">
                      {fmtHM(v.debut)} → {fmtHM(v.fin)}
                    </p>
                    {v.posteLibelle ? (
                      <p className="mt-1 text-xs text-navy-600">
                        {v.posteLibelle}
                      </p>
                    ) : null}
                  </li>
                ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function Card({
  label,
  value,
  accent,
  small,
}: {
  label: string;
  value: string;
  accent?: boolean;
  small?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-navy-100 bg-white p-3 shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-wider text-navy-500">
        {label}
      </p>
      <p
        className={`mt-1 font-bold ${accent ? "text-accent-700" : "text-navy-800"} ${
          small ? "text-sm" : "text-2xl"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function fmtHM(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
